// src/pages/website/HomePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';
import './website.css';

const FEATURES = [
  { icon: '⚡', title: 'Instant Access', desc: 'Anyone with a smartphone can scan and get emergency contact info — no app needed.' },
  { icon: '🔒', title: 'Privacy First', desc: 'Only essential emergency info shared. Home address and sensitive data never shown.' },
  { icon: '📱', title: 'Works Offline', desc: 'QR code itself needs no internet. Scanner just needs a camera.' },
  { icon: '🏥', title: 'Medical Info', desc: 'Blood group, allergies and conditions instantly visible in emergencies.' },
  { icon: '📞', title: 'One-tap Call', desc: 'Emergency contact reachable with a single tap — no need to type a number.' },
  { icon: '🔄', title: 'Always Updated', desc: 'Update contact info anytime — QR code stays the same.' },
];

const USE_CASES = [
  { icon: '🎒', title: 'School Children', desc: 'Attach to school bag or ID card' },
  { icon: '🚗', title: 'Vehicles', desc: 'Stick on windshield or helmet' },
  { icon: '👴', title: 'Senior Citizens', desc: 'Carry in wallet or wear as tag' },
  { icon: '🏫', title: 'Schools', desc: 'Bulk QR for all students' },
  { icon: '🏥', title: 'Hospitals', desc: 'Patient wristbands' },
  { icon: '🏢', title: 'Corporates', desc: 'Employee ID cards' },
  { icon: '🕌', title: 'Pilgrimage', desc: 'Visitor safety tags' },
  { icon: '🏠', title: 'Old Age Homes', desc: 'Resident wristbands' },
];

const STEPS = [
  { icon: '📲', title: 'Download App', desc: 'Install CivicSetu on Android. Register with your phone number.' },
  { icon: '👤', title: 'Add Profile', desc: 'Add your vehicle or family member with emergency contact details.' },
  { icon: '🎯', title: 'Generate QR', desc: 'Generate your unique QR code. Download or order printed version.' },
  { icon: '🛡️', title: 'Stay Safe', desc: 'Attach QR to vehicle, bag or ID card. Help reaches you instantly.' },
];

export default function HomePage() {
  return (
    <div className="website-page">
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            🇮🇳 Built for India's safety needs
          </div>
          <h1>
            Emergency Help in<br />
            <span>One Scan</span>
          </h1>
          <p>
            CivicSetu QR codes let first responders instantly contact
            your family in emergencies — for vehicles, children, senior
            citizens and more.
          </p>
          <div className="hero-buttons">
            <a
              href="https://play.google.com/store/apps/details?id=com.civicsetu.app"
              target="_blank"
              rel="noreferrer"
              className="web-btn web-btn-accent web-btn-lg"
            >
              📱 Download Free App
            </a>
            <Link to="/bulk/login" className="web-btn web-btn-outline web-btn-lg"
              style={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff' }}
            >
              🏫 Bulk Orders for Institutions
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <h3>150K+</h3>
              <p>Road accidents/year</p>
            </div>
            <div className="hero-stat">
              <h3>60 min</h3>
              <p>Golden hour</p>
            </div>
            <div className="hero-stat">
              <h3>1 scan</h3>
              <p>To save a life</p>
            </div>
          </div>
        </div>

        <div className="hero-image">
          <div className="hero-qr-demo">
            <div className="qr-placeholder" />
            <div className="hero-qr-label">🛡️ CivicSetu Emergency QR</div>
          </div>
          <div style={{ fontSize: 13, opacity: 0.8, lineHeight: 1.6 }}>
            Scan any CivicSetu QR code to instantly see emergency contact info
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section className="section" id="how-it-works">
        <div className="section-header">
          <div className="section-label">How It Works</div>
          <h2>Safety in 4 Simple Steps</h2>
          <p>Get your emergency QR code in minutes. No technical knowledge needed.</p>
        </div>
        <div className="steps-grid">
          {STEPS.map((step, i) => (
            <div className="step-card" key={i}>
              <div className="step-icon">{step.icon}</div>
              <div className="step-number">{i + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section className="section section-alt" id="features">
        <div className="section-header">
          <div className="section-label">Features</div>
          <h2>Everything You Need for Safety</h2>
          <p>Designed specifically for India's emergency response needs.</p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Use Cases ────────────────────────────────────────── */}
      <section className="section" id="use-cases">
        <div className="section-header">
          <div className="section-label">Use Cases</div>
          <h2>Who Uses CivicSetu?</h2>
          <p>From families to institutions — safety for everyone.</p>
        </div>
        <div className="use-cases-grid">
          {USE_CASES.map((u, i) => (
            <div className="use-case-card" key={i}>
              <div className="use-case-icon">{u.icon}</div>
              <h3>{u.title}</h3>
              <p>{u.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing Preview ──────────────────────────────────── */}
      <section className="section section-alt">
        <div className="section-header">
          <div className="section-label">Pricing</div>
          <h2>Simple, Affordable Pricing</h2>
          <p>Starting at just ₹10. Bulk discounts for institutions.</p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Digital QR</h3>
            <p style={{ color: '#666', fontSize: 14 }}>Download and print yourself</p>
            <div className="price">₹10 <span>/ QR</span></div>
            <div className="price-note">One-time payment</div>
            <ul className="pricing-features">
              <li>High resolution PNG download</li>
              <li>Phone back cover card format</li>
              <li>Keychain / bag tag format</li>
              <li>Instant delivery</li>
            </ul>
            <a
              href="https://play.google.com/store/apps/details?id=com.civicsetu.app"
              className="web-btn web-btn-outline"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Get Started
            </a>
          </div>

          <div className="pricing-card featured">
            <div className="pricing-badge">Most Popular</div>
            <h3>Physical QR</h3>
            <p style={{ color: '#666', fontSize: 14 }}>Printed and couriered to you</p>
            <div className="price">₹399 <span>/ QR</span></div>
            <div className="price-note">Free shipping across India</div>
            <ul className="pricing-features">
              <li>Professional print quality</li>
              <li>Paper (laminated) or Vinyl sticker</li>
              <li>Weatherproof vinyl option</li>
              <li>Delivered in 3-5 business days</li>
            </ul>
            <a
              href="https://play.google.com/store/apps/details?id=com.civicsetu.app"
              className="web-btn web-btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Order Now
            </a>
          </div>

          <div className="pricing-card">
            <h3>Bulk Orders</h3>
            <p style={{ color: '#666', fontSize: 14 }}>For schools and institutions</p>
            <div className="price">₹4 <span>/ QR</span></div>
            <div className="price-note">For orders above 500 QRs</div>
            <ul className="pricing-features">
              <li>CSV/Excel upload</li>
              <li>Up to 60% discount</li>
              <li>GST invoice included</li>
              <li>ZIP download of all QRs</li>
            </ul>
            <Link
              to="/bulk/login"
              className="web-btn web-btn-accent"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Order in Bulk
            </Link>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/pricing" style={{ color: 'var(--primary)', fontSize: 14 }}>
            View full pricing details →
          </Link>
        </div>
      </section>

      {/* ── India Stats ───────────────────────────────────────── */}
      <section className="section" style={{ background: '#1a1a2e', color: '#fff' }}>
        <div className="section-header">
          <div className="section-label" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
            Why It Matters
          </div>
          <h2 style={{ color: '#fff' }}>India Needs This Now</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>
            Every minute counts in an emergency.
          </p>
        </div>
        <div className="stats-grid" style={{ maxWidth: 800, margin: '0 auto' }}>
          {[
            { num: '1.5L+', label: 'Road accident deaths per year', icon: '🚗' },
            { num: '60 min', label: 'Golden hour for trauma survival', icon: '⏰' },
            { num: '40%', label: 'Victims unidentified at scene', icon: '❓' },
            { num: '1 scan', label: 'Is all it takes with CivicSetu', icon: '✅' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 12,
              padding: '24px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#7dd3fc' }}>{s.num}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Download CTA ─────────────────────────────────────── */}
      <section className="download-section">
        <h2>Start Protecting Your Family Today</h2>
        <p>Free to download. Takes 2 minutes to set up. Could save a life.</p>
        <div className="download-buttons">
          <a
            href="https://play.google.com/store/apps/details?id=com.civicsetu.app"
            target="_blank"
            rel="noreferrer"
            className="download-btn"
          >
            <span className="download-btn-icon">▶</span>
            <div className="download-btn-text">
              <small>Get it on</small>
              <strong>Google Play</strong>
            </div>
          </a>
          <Link to="/bulk/login" className="download-btn">
            <span className="download-btn-icon">🏫</span>
            <div className="download-btn-text">
              <small>For institutions</small>
              <strong>Bulk Orders</strong>
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
